import { Card, Stack, Table, TableBody, TableContainer } from '@mui/material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { isAfter } from 'src/utils/format-time';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { useBoolean } from 'src/hooks/use-boolean';
import usePagination from 'src/hooks/use-pagination';
import axios, { endpoints } from 'src/utils/axios';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import { TransactionType, TransactionStatus, TransactionBadgeType } from 'src/type/transaction';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';

import { UserType } from 'src/type/user';
import {
  TopupTxType,
  TopupFilters,
  TopupFilterKey,
  TopupFilterValue,
} from 'src/type/topup-deposit';
import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
import LoadingDialog from 'src/components/ui-kit/loading-dialog';
import { enqueueSnackbar } from 'notistack';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import TopupTableFiltersResult from './topup-table-filters-result';
import TopupTableRow from './topup-table-row';
import TopupTableToolbar from './topup-table-toolbar';
import TopupCreateDialog from './topup-create-dialog';

const defaultFilters: TopupFilters = {
  name: null,
  startDate: null,
  endDate: null,
  status: 'all',
};
const tableHead = [
  { id: 'ลูกค้า', label: 'ลูกค้า' },
  { id: 'sent', label: 'จำนวนเงิน', width: 120 },
  { id: 'createDate', label: 'วันที่ทำรายการ', width: 180 },
  { id: 'dueDate', label: 'วันที่อนุมัติ', width: 160 },
  { id: 'status', label: 'สถานะ', width: 120 },
  { id: 'action', width: 60 },
];
type Props = {
  user?: UserType;
  enableCreate?: boolean;
  isCreateAndApprove?: boolean;
  rootReload?: () => void;
};

export default function TopupDepositTableView({
  user,
  enableCreate = false,
  isCreateAndApprove = false,
  rootReload,
}: Props) {
  const initialize = useBoolean(true);
  const loading = useBoolean();
  const approve = useBoolean();

  const [filters, setFilters] = useState(defaultFilters);

  // const [singleTx, setSingleTx] = useState<TopupTxType | null>(null);

  const [badge, setBadge] = useState<TransactionBadgeType>({
    total: 0,
    waiting: 0,
    pending: 0,
    success: 0,
    rejected: 0,
    cancel: 0,
    failed: 0,
  });

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  const form = useBoolean(false);
  const reload = useBoolean(false);

  const canReset =
    !!filters.name || filters.status !== 'all' || (!!filters.startDate && !!filters.endDate);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<TopupTxType>();

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      ...fDateRange(filters.startDate, filters.endDate),

      type: TransactionType.TOPUP_DEPOSIT,
      status: filters.status === 'all' ? undefined : filters.status,
      page: currentPage,
      limit: rowsPerPage,
      userId: user?.uid || filters.name?.value,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetInviteCode(params);
  }, [
    currentPage,
    rowsPerPage,
    filters.startDate,
    filters.endDate,
    filters.status,
    reload.value,
    filters.name,
  ]);

  const handleGetInviteCode = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data: _badge } = await axios.get<TransactionBadgeType>(
        endpoints.transactions.badges,
        {
          params: {
            type: TransactionType.TOPUP_DEPOSIT,
          },
        }
      );
      setBadge(_badge);
      const { data } = await axios.get<Pagination<TopupTxType>>(endpoints.transactions.get, {
        params: fParams(params),
      });
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  // const handleGetTransactionById = useCallback(async (id: string) => {
  //   try {
  //     loading.onTrue();

  //     const { data } = await axios.get<TopupTxType>(endpoints.transactions.getById(id));
  //     setSingleTx(data);
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     loading.onFalse();
  //   }
  // }, []);

  const currentDateTime = useCurrentDateTime();
  const onApprove = async (txId: string) => {
    try {
      loading.onTrue();

      await axios.post(endpoints.transactions.topupDeposit.approve(txId), {
        note: '',
        currentDateTime,
      });
      if (isCreateAndApprove) {
        handleReload();
      }
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  };

  const handleFilters = useCallback((name: TopupFilterKey, value: TopupFilterValue) => {
    onResetPage();
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    if (rootReload) {
      rootReload();
    }
    reload.onToggle();
  }, []);

  const tabs: StatusTabFilterOption = useMemo(
    () => [
      { value: 'all', label: 'ทั้งหมด', color: 'default', count: badge.total },
      {
        value: TransactionStatus.SUCCESS,
        label: 'สำเร็จ',
        color: 'success',
        count: badge.success,
      },
      {
        value: TransactionStatus.PENDING,
        label: 'รอดำเนินการ',
        color: 'warning',
        count: badge.pending,
      },
      {
        value: TransactionStatus.REJECTED,
        label: 'ปฏิเสธ',
        color: 'error',
        count: badge.rejected,
      },
      // {
      //   value: TransactionStatus.CANCEL,
      //   label: 'ยกเลิก',
      //   color: 'info',
      //   count: badge.cancel,
      // },
    ],
    [badge]
  );

  return (
    <>
      <Stack spacing={3}>
        <Card>
          {!user && (
            <StatusTabFilter
              value={filters.status}
              onChange={(newValue) => handleFilters('status', newValue)}
              options={tabs}
            />
          )}

          <TopupTableToolbar
            filters={filters}
            onFilters={handleFilters}
            dateError={dateError}
            disabledSearch={!!user}
            enableCreate={enableCreate || !!user}
            onCreateTopup={form.onTrue}
          />

          {canReset && (
            <TopupTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={meta.totalResults}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <TopupTableRow key={row.id} row={row} handleReload={handleReload} />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>
      {!!form.value && (!!enableCreate || !!user) && (
        <TopupCreateDialog
          open={form.value}
          onClose={form.onFalse}
          user={enableCreate && !user ? null : user || null}
          handleReload={handleReload}
          isCreateAndApprove={isCreateAndApprove}
          openApproveDialog={(txId) => {
            (async () => {
              if (txId) {
                // await handleGetTransactionById(txId);
                await onApprove(txId);
                approve.onTrue();
              }
            })();
          }}
        />
      )}

      {/* {!!approve.value && !!singleTx && (
        <TopupApproveDialog
          isCreateAndApprove={isCreateAndApprove}
          open={approve.value}
          onClose={approve.onFalse}
          handleReload={handleReload}
          tx={singleTx}
        />
      )} */}

      <LoadingDialog open={loading.value} />
    </>
  );
}
